import * as React from 'react';
import { Grid, Segment } from 'semantic-ui-react';
import { Tab, Tabs } from '@blueprintjs/core';
import DateRangePickerComponent from '../../../shared/component/date_range_component';
import MultiSelectDropDownComponent from '../../../shared/component/multi_select_dropdown_component';
import Selection from '../../../shared/component/selection_component';
import MultiSelectPanelComponent from '../../../shared/component/multi_select_panel_component';
import { AppliedModifierFormData, AppliedModifierDropdownData, AppliedModifierPanelData } from '../../constant/applied_modifiers_constants';
import * as AppliedModifiersConstants from '../../constant/applied_modifiers_constants';
import { UI_CONSTANTS } from '../../constant/applied_modifiers_constants';
import * as SharedConstants from '../../../shared/shared_constants';
import { custom_date_format, toBoolean } from '../../../shared/utility';
import * as global_constants from '../../../global_constants';
import moment = require('moment');

export interface WhenSectionProps {
  formData: AppliedModifierFormData;
  onFieldChange: (field: string, value: any) => void;
  markFieldForRequiredValidation: (fieldName: string) => boolean;
  markFieldForValidation: (fieldName: string) => boolean;
  dropdownData: AppliedModifierDropdownData;
  panelData: AppliedModifierPanelData;
  isSaveButtonDisabled: boolean;
  insuranceClassTabDisabled: boolean;
  excludeInsuranceClassTabDisabled: boolean;
  hasDateOfServiceRangeError: boolean;
  onDateOfServiceRangeHasError: (hasError: boolean) => void;
  getDisplayFormatDate: (date: any) => any;
  convertInDateFormat: (date: any) => string | null;
  isSubmitted: boolean;
}

export const WhenSection: React.FC<WhenSectionProps> = ({
  formData,
  onFieldChange,
  markFieldForRequiredValidation,
  markFieldForValidation,
  dropdownData,
  panelData,
  isSaveButtonDisabled,
  insuranceClassTabDisabled,
  excludeInsuranceClassTabDisabled,
  hasDateOfServiceRangeError,
  onDateOfServiceRangeHasError,
  getDisplayFormatDate,
  convertInDateFormat,
  isSubmitted
}) => {
  const onChangeDateOfServiceRange = (startDate: any, endDate: any) => {
    onFieldChange('start_date', convertInDateFormat(startDate));
    onFieldChange('end_date', convertInDateFormat(endDate));
  };

  // Direct validation check for date range - mimics claims_scrubbing_procedure_replacement_add_edit_new_component.tsx line 780 & 786
  // Only shows error if: start_date is missing (required) AND form has been submitted
  // Does NOT check end_date (optional)
  const getDateRangeValidationError = () => {
    const hasValidationError = !formData.start_date;
    return hasValidationError && isSubmitted;
  };

  const onChangeState = (key_id: any, selected_values: any[]) => {
    onFieldChange('state', selected_values || []);
  };

  const onChangeProviderType = (value: string, e: any) => {
    onFieldChange('provider_type', value);
    
    // If provider type is "None", clear primary specialty selection
    if (value === AppliedModifiersConstants.ProviderTypeValues.NONE) {
      onFieldChange('primary_specialty', []);
    }
  };

  const onChangePrimarySpecialty = (key_id: any, selected_values: any[]) => {
    onFieldChange('primary_specialty', selected_values);
  };

  const onChangePayer = (selected_values: any[]) => {
    onFieldChange('payer', selected_values);
  };

  const onChangeInsuranceClass = (selected_values: any[]) => {
    onFieldChange('insurance_class', selected_values);
  };

  const onChangeExcludedInsuranceClass = (selected_values: any[]) => {
    onFieldChange('excluded_insurance_class', selected_values);
  };

  const getPayersMultiSelectPanel = () => {
    return (
      <div className="criteria_panel">
        <MultiSelectPanelComponent
          id={AppliedModifiersConstants.field_identifier.payer_panel}
          data={panelData.payer_list}
          selected_list={formData.payer || []}
          onChangeSelected={onChangePayer}
          is_save_in_progress={isSaveButtonDisabled}
          displayExtended={true}
        />
      </div>
    );
  };

  const getInsuranceClassMultiSelectPanel = () => {
    return (
      <div className="criteria_panel">
        <MultiSelectPanelComponent
          id={AppliedModifiersConstants.field_identifier.insurance_class_panel}
          data={panelData.insurance_class_list}
          selected_list={formData.insurance_class || []}
          onChangeSelected={onChangeInsuranceClass}
          is_save_in_progress={isSaveButtonDisabled}
          displayExtended={true}
        />
      </div>
    );
  };

  const getExcludedInsuranceClassMultiSelectPanel = () => {
    return (
      <div className="criteria_panel">
        <MultiSelectPanelComponent
          id={AppliedModifiersConstants.field_identifier.excluded_insurance_class_panel}
          data={panelData.insurance_class_list}
          selected_list={formData.excluded_insurance_class || []}
          onChangeSelected={onChangeExcludedInsuranceClass}
          is_save_in_progress={isSaveButtonDisabled}
          displayExtended={true}
        />
      </div>
    );
  };

  // Helper methods for conditional validation display
  const isProviderTypeSelected = formData.provider_type && 
                               formData.provider_type !== AppliedModifiersConstants.ProviderTypeValues.NONE;

  const isPrimarySpecialtySelected = formData.primary_specialty && 
                                   Array.isArray(formData.primary_specialty) && 
                                   formData.primary_specialty.length > 0;

  return (
    <>
      <Grid.Row>
        <Grid.Column width={16}>
          <label className='applied_modifiers_ui_section_label'>
            <span>
              WHEN
            </span>
          </label>
        </Grid.Column>
        <Grid.Column width={8}>
          <label>
            Date of Service Range
            <span className={getDateRangeValidationError() ? "req-alert" : 'req-alert_normal'}> (required)</span>
          </label>
          <DateRangePickerComponent
            updateDatesChange={onChangeDateOfServiceRange}
            startDate={getDisplayFormatDate(formData.start_date)}
            endDate={getDisplayFormatDate(formData.end_date)}
            id={AppliedModifiersConstants.field_identifier.date_of_service_range}
            error={getDateRangeValidationError()}
            errorDisplayType={SharedConstants.enum_date_range_error_display_type.UNDERCOMPONENT}
            updateHasError={onDateOfServiceRangeHasError}
            disableClearOnValidation={true}
            data-testid={AppliedModifiersConstants.field_identifier.date_of_service_range}
          />
        </Grid.Column>
        <Grid.Column width={8}>
          <label>
            State
          </label>
          <MultiSelectDropDownComponent
            data={dropdownData.state_list}
            class_name='manage-content-multi-select'
            id={AppliedModifiersConstants.field_identifier.state}
            key={AppliedModifiersConstants.field_identifier.state}
            key_id={AppliedModifiersConstants.field_identifier.state}
            onChangeSelected={onChangeState}
            selected_list={formData.state || []}
            data-testid={AppliedModifiersConstants.field_identifier.state}
            forceOnChangeSelected={true}
            has_validation={false}
            show_filter={true}
            reset_disable={false}
            include_future_selected={false}
            disabled={false}
            include_eye_feature={true}
          />
        </Grid.Column>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column width={8}>
          <label>
            Provider Type
            {isPrimarySpecialtySelected ? 
              <span className={markFieldForRequiredValidation(AppliedModifiersConstants.field_identifier.provider_type) ? "req-alert" : 'req-alert_normal'}> (required)</span> 
              : null}
          </label>
          <Selection
            id={AppliedModifiersConstants.field_identifier.provider_type}
            name={AppliedModifiersConstants.field_identifier.provider_type}
            options={AppliedModifiersConstants.provider_type_list}
            defaultValue={formData.provider_type}
            isRequired={markFieldForValidation(AppliedModifiersConstants.field_identifier.provider_type)}
            onChange={onChangeProviderType}
            data-testid={AppliedModifiersConstants.field_identifier.provider_type}
          />
        </Grid.Column>
        <Grid.Column width={8}>
          <label>
            Primary Specialty
            {isProviderTypeSelected ? 
              <span className={markFieldForRequiredValidation(AppliedModifiersConstants.field_identifier.primary_specialty) ? "req-alert" : 'req-alert_normal'}> (required)</span> 
              : null}
          </label>
          <MultiSelectDropDownComponent
            data={dropdownData.primary_specialty_list}
            class_name={`manage-content-multi-select ${markFieldForValidation(AppliedModifiersConstants.field_identifier.primary_specialty) ? 'req-background-inp' : ''}`}
            id={AppliedModifiersConstants.field_identifier.primary_specialty}
            key={AppliedModifiersConstants.field_identifier.primary_specialty}
            key_id={AppliedModifiersConstants.field_identifier.primary_specialty}
            onChangeSelected={onChangePrimarySpecialty}
            selected_list={formData.primary_specialty || []}
            data-testid={AppliedModifiersConstants.field_identifier.primary_specialty}
            forceOnChangeSelected={true}
            has_validation={false}
            show_filter={true}
            reset_disable={false}
            include_future_selected={false}
            disabled={false}
            include_eye_feature={true}
          />
        </Grid.Column>
      </Grid.Row>
      <Grid.Row className='last-row applied_modifiers_tabs_row'>
        <Segment className='applied_modifiers_tabs_segment'>
          <Grid.Column className='applied_modifiers_tabs_column' data-testid={AppliedModifiersConstants.field_identifier.tabs_wrapper}>
            <Tabs animate={true} id={AppliedModifiersConstants.field_identifier.tabs_wrapper} className="bp3-fill">
              <Tab
                id={AppliedModifiersConstants.field_identifier.payer}
                title='Payer'
                panel={getPayersMultiSelectPanel()}
                data-testid={AppliedModifiersConstants.field_identifier.payer}
              />
              <Tab
                id={AppliedModifiersConstants.field_identifier.insurance_class}
                disabled={insuranceClassTabDisabled}
                title="Insurance Class"
                panel={getInsuranceClassMultiSelectPanel()}
                data-testid={AppliedModifiersConstants.field_identifier.insurance_class}
              />
              <Tab
                id={AppliedModifiersConstants.field_identifier.excluded_insurance_class}
                disabled={excludeInsuranceClassTabDisabled}
                title="Excluded Insurance Class"
                panel={getExcludedInsuranceClassMultiSelectPanel()}
                data-testid={AppliedModifiersConstants.field_identifier.excluded_insurance_class}
              />
              <label>
                <span className={markFieldForRequiredValidation(AppliedModifiersConstants.field_identifier.tabs_wrapper) ? "req-alert" : 'req-alert_normal'}> (required)</span>
              </label>
              <Tabs.Expander />
            </Tabs>
          </Grid.Column>
        </Segment>
      </Grid.Row>
    </>
  );
};
